import { createContext, FC, PropsWithChildren, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { DesignCompany } from './types';
import { useTranslation } from 'react-i18next';
import { localeToOption } from '@/features/workspaceEdit/components/LocalizationSelector/helpers';
import i18n from '@/localization';
import { LocalizationTitle } from '@/features/workspaceEdit/components/LocalizationSelector/constants';

const themes = import.meta.glob('/src/styles/themes/*/index.css');

export type CompanyValuesApi = {
  company: string;
  isDefault: boolean;
  isExternal: boolean;
  currentLanguage: string;
};

type CompanyActionsApi = {
  setCompany: (company: DesignCompany) => void;
};

export const CompanyValuesContext = createContext<CompanyValuesApi | null>(null);
export const CompanyActionsContext = createContext<CompanyActionsApi | null>(null);

export const CompanyProvider: FC<PropsWithChildren> = ({ children }) => {
  const [company, setCompany] = useState<DesignCompany>((import.meta.env.VITE_COMPANY as DesignCompany) ?? 'audiencerate');
  const isDefault = company === 'audiencerate';
  const isExternal = company !== 'audiencerate';

  const { t } = useTranslation();

  const currentLanaguage = useMemo(() => {
    const temp = localeToOption(i18n.language);

    return {
      ...temp,
      label:
        t(`localization:${temp.label as LocalizationTitle}`)
          .charAt(0)
          .toUpperCase() + t(`localization:${temp.label as LocalizationTitle}`).slice(1),
    };
  }, [i18n.language]);

  useLayoutEffect(() => {
    if (company) {
      const path = `/src/styles/themes/${company}/index.css`;
      const loadTheme = themes[path];

      if (loadTheme) {
        loadTheme();
      }

      document.documentElement.setAttribute('data-company', company);
    }
  }, [company]);

  const value = useMemo(
    () =>
      ({
        company,
        isDefault,
        isExternal,
        currentLanguage: (currentLanaguage?.id as string) || 'en',
      }) satisfies CompanyValuesApi,
    [company, isDefault, isExternal, currentLanaguage]
  );

  const actions = useMemo(
    () =>
      ({
        setCompany,
      }) satisfies CompanyActionsApi,
    [setCompany]
  );

  return (
    <CompanyValuesContext.Provider value={value}>
      <CompanyActionsContext.Provider value={actions}>{children}</CompanyActionsContext.Provider>
    </CompanyValuesContext.Provider>
  );
};

// Create hooks for easier consumption
export const useCompanyValues = () => {
  const context = useContext(CompanyValuesContext);
  if (!context) {
    throw new Error('useCompanyValues must be used within a CompanyProvider');
  }
  return context;
};
